<!-- AnchoredPanel.vue -->
<!--
  Painel ancorado fora do fluxo: teleportado para o body, `position: fixed`,
  posicionado a partir do gatilho (ver useAnchoredPanel). Use para TODO painel
  que abre a partir de um elemento - sub-painel de item de menu, popover de
  filtro, seletor - em vez de `position: absolute` dentro do gatilho: qualquer
  ancestral com `overflow` (inclusive o próprio menu canônico) o recortaria.

  Uso, no lugar onde o painel em `absolute` ficaria:
    <AnchoredPanel :open="aberto" placement="side" @close="aberto = false">
      <div class="meu-painel">...</div>
    </AnchoredPanel>

  Props:
    open      - abre/fecha (controlado por quem usa)
    anchor    - elemento âncora; por padrão, o elemento pai de onde o
                componente foi colocado (a linha do menu, o botão)
    placement - "below" (padrão) ou "side" (ao lado: sub-painel de menu)
    align     - ver useAnchoredPanel
    gap       - distância do gatilho, em px

  Emits:
    close     - o painel se fechou sozinho (clique fora, Esc)
    opened    - o painel já está no DOM e posicionado (hora de dar foco)
-->
<script setup>
import { onMounted, ref, watch } from "vue";
import { useAnchoredPanel } from "@/composables/useAnchoredPanel";

defineOptions({ inheritAttrs: false });

const props = defineProps({
  open:      { type: Boolean, default: false },
  anchor:    { type: null, default: null },
  placement: { type: String, default: "below" },
  align:     { type: String, default: undefined },
  gap:       { type: Number, default: 4 },
});

const emit = defineEmits(["close", "opened"]);

// Marcador invisível no lugar do componente: o pai dele é a âncora padrão.
const marker = ref(null);

const { isOpen, triggerRef, panelRef, panelStyle, open, close } = useAnchoredPanel({
  placement: props.placement,
  align: props.align,
  gap: props.gap,
  onClose: () => emit("close"),
});

async function sync(shouldOpen) {
  if (shouldOpen) {
    triggerRef.value = props.anchor ?? marker.value?.parentElement ?? null;
    await open();
    if (isOpen.value) emit("opened");
  } else {
    close();
  }
}

watch(() => props.open, sync);
onMounted(() => props.open && sync(true));
</script>

<template>
  <span ref="marker" hidden></span>
  <Teleport to="body">
    <div v-if="isOpen" ref="panelRef" class="anchored-panel" :style="panelStyle" v-bind="$attrs">
      <slot />
    </div>
  </Teleport>
</template>
